import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, Sparkles, TrendingUp } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { LabThroughputWidget } from "@/components/LabThroughputWidget";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { TRADE } from "@/constants/testIds";

export default function TradeDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/trade/orders").then(({ data }) => setOrders(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const totalSpend = orders.reduce((s, o) => s + (o.total || 0), 0);
  const totalSamples = orders.reduce((s, o) => s + (o.quantity || 0), 0);
  const isLabStaff = user?.role === "admin" || user?.role === "lab_technician";

  return (
    <PortalShell title={`Welcome, ${user?.full_name.split(" ")[0]}`} subtitle="Trade Partner Dashboard">
      {isLabStaff && <LabThroughputWidget />}
      <div data-testid={TRADE.dashboard} className="grid lg:grid-cols-3 gap-5">
        <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center"><Package className="w-5 h-5 text-cyan-600" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Orders</span>
          </div>
          <div className="mt-5 font-display text-4xl font-bold text-slate-900">{orders.length}</div>
          <p className="text-sm text-slate-500 mt-1">Total trade orders placed</p>
        </div>
        <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center"><Sparkles className="w-5 h-5 text-cyan-600" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Samples</span>
          </div>
          <div className="mt-5 font-display text-4xl font-bold text-slate-900">{totalSamples}</div>
          <p className="text-sm text-slate-500 mt-1">Total samples tested</p>
        </div>
        <div className="rounded-3xl bg-slate-900 text-white p-7 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-cyan-400" /></div>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-400">Spend</span>
          </div>
          <div className="mt-5 font-display text-4xl font-bold">${totalSpend.toFixed(2)}</div>
          <p className="text-sm text-slate-300 mt-1">Wholesale lifetime spend</p>
        </div>
      </div>

      <div className="mt-10 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
        <h2 className="font-display text-2xl font-semibold text-slate-900">Recent orders</h2>
        <div className="flex flex-wrap gap-2">
          <Link
            data-testid="trade-nav-earnings"
            to="/trade/earnings"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 text-slate-700 text-sm font-semibold transition-colors"
          >
            <Sparkles className="w-4 h-4" /> My earnings
          </Link>
          <Link
            data-testid="trade-nav-certificates"
            to="/trade/certificates"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 text-slate-700 text-sm font-semibold transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Certificates
          </Link>
          <Link
            data-testid="trade-nav-branding"
            to="/trade/branding"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 hover:border-cyan-500 hover:text-cyan-700 text-slate-700 text-sm font-semibold transition-colors"
          >
            <Package className="w-4 h-4" /> White-label
          </Link>
          <Link
            data-testid={TRADE.navPlaceOrder}
            to="/trade/order"
            className="btn-aqua inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Place new order
          </Link>
        </div>
      </div>

      <div className="mt-5 rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-slate-600">No orders yet.</p>
            <Link to="/trade/order" className="mt-3 inline-block font-semibold text-cyan-600 hover:underline">Place your first wholesale order →</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Reference</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Test</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Qty</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Client</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Total</th>
                <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 10).map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="p-4 font-mono text-xs font-semibold text-slate-900">{o.reference}</td>
                  <td className="p-4 text-slate-700">{o.test_name}</td>
                  <td className="p-4 font-semibold">{o.quantity}</td>
                  <td className="p-4 text-slate-600">{o.client_name}{o.white_label && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-900 text-cyan-300">WHITE LABEL</span>}</td>
                  <td className="p-4 font-semibold text-slate-900">${o.total.toFixed(2)}</td>
                  <td className="p-4">
                    <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold capitalize">{o.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PortalShell>
  );
}
